/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import RemoteCase, { REMOTE_CASE_VIEW } from '../case/remote-case';
import CaseUtils from '../case/utils';
import type { ActionObject, ExtendedActionObject } from '../globals';
import { IsLoadingManagerInstance } from '../utils/loading-manager';
import * as actionType from '../store/state/actions/index';

interface ActionError {
  type?: string;
  message?: string;
  details?: unknown;
}

/**
 * The <strong>Action Manager</strong> object provides access to methods
 * that are used by Actions (ex: as found in Actions-Api or ContainerManager)
 * to track promises that are returned by the action.
 */

class ActionManager<T = unknown> {
  private actionMgrActions: {
    [key: string]: {
      actionType: string;
      promise: Promise<T>;
      resolve: (value: T) => void;
      reject: (reason?: ActionError) => void;
      actionPayload: ActionObject | undefined;
    };
  };

  private counterActionMgrId: number;
  /**
   * Constructor - <strong>Not for use outside of Core! Included for reference only.</strong>
   */
  constructor() {
    // local variable used to compute the next actionMgrID
    this.counterActionMgrId = 0;
    // local array used to store the association of an actionMgrID to its associated Promise
    //  each entry is: { <actionMgrID>: <the actionMgrID's associated Promise>}
    this.actionMgrActions = {};
  }

  /**
   * Returns a unique (for this session) actionMgrId that should
   * be provided in the payload to StateManager.dispatch so the
   * ActionManager can use it
   * @returns the next actionMgrID
   * @private
   */
  getNextActionMgrId(): string {
    this.counterActionMgrId += 1;
    // Note that we use the string version of the number, so we have an
    //  associative array that we can clean up later, if needed.
    return this.counterActionMgrId.toString();
  }

  /**
   * Registers the given Promise with the given actionMgrID
   * @param theActionMgrID the actionMgrID that is associated with the given Promise
   * @param thePromise the Promise associated with the given actionMgrID
   * @param theResolve the function called when the promise resolves
   * @param theReject the function called when the promise rejects
   * @param theActionType the type of action associated with this Promise
   * @param actionConfig the action config which has the context information
   * @param enableLoadingIndicator a boolean flag which tells if the actions need a loading indicator or not
   * @returns returns the registered ID if register succeeded or null if it failed (ex: ID already been used)
   * @private
   */
  register(
    theActionMgrID: string,
    thePromise: Promise<T>,
    theResolve: (value: T) => void,
    theReject: (reason?: ActionError) => void,
    theActionType: string = 'unknown',
    actionConfig?: ActionObject,
    enableLoadingIndicator: boolean = true
  ) {
    if (this.actionMgrActions[theActionMgrID] !== undefined) {
      return null;
    }
    const thePayload = {
      actionType: theActionType,
      promise: thePromise,
      resolve: theResolve,
      reject: theReject,
      actionPayload: actionConfig
    };
    this.actionMgrActions[theActionMgrID] = thePayload;
    if (enableLoadingIndicator) {
      IsLoadingManagerInstance.enableLoadingIndicator(thePayload.actionPayload);
    }
    return theActionMgrID;
  }

  /**
   * Create a promise for an action and return an object containing the promise
   * and the ActionManager actionMgrID associated with that promise
   * @param theActionType the action type for which we're creating a promise
   * @param theActionMgrID the Action Manager ID associated with the promise. If provided,
   * the given ID is used when then promise is registered with the ActionManager. If not provided, we get the
   * ID and use the ID we get.
   * @returns object with actionMgrID and promise keys: { actionMgrID: <theID>, promise: <thePromise> }
   * @function
   */

  actionManagerPromise = (theActionType: string, theActionMgrID: string = '') => {
    if (theActionMgrID === '') {
      theActionMgrID = this.getNextActionMgrId();
    }
    let resolveFn;
    let rejectFn;
    const thePromise = new Promise<T>((resolve, reject) => {
      resolveFn = resolve;
      rejectFn = reject;
    });

    if (resolveFn && rejectFn) {
      this.register(theActionMgrID, thePromise, resolveFn, rejectFn, theActionType);
    } else {
      throw new Error('resolveFn or rejectFn is not defined.');
    }

    return {
      actionMgrID: theActionMgrID,
      promise: thePromise
    };
  };

  /**
   * resolve the promise associated with the given ID
   * @param theActionMgrID the ID of the Action Manager entry that should be resolved
   * @param data metadata info about the view
   * @param allowDispatch flag that dictates if Reset_Loading redux action should be dispatched or not
   * @returns returns the resolved ID if resolve was called or null if problem was encountered
   * @private
   */
  resolveActionByID(theActionMgrID: string | number, data?: T, allowDispatch = true) {
    const thePayload = this.actionMgrActions[theActionMgrID];
    let theReturn: string | number | null = theActionMgrID;

    if (thePayload) {
      // [TODO] : Need to find a better way, instead of using setTimeouts.
      setTimeout(() => {
        // Call the promise resolve function that was passed in as part of other payload
        thePayload.resolve(data as T);
      }, 0);
      IsLoadingManagerInstance.disableLoadingIndicator(thePayload.actionPayload, allowDispatch);
    } else {
      // no payload found!
      theReturn = null;
    }

    return theReturn;
  }

  /**
   * reject the promise associated with the given ID
   * @param theActionMgrID the ID of the Action Manager entry that should be resolved
   * @returns returns the rejected ID if reject was called or null if problem was encountered
   * @param allowDispatch flag that dictates if Reset_Loading redux action should be dispatched or not
   * @param error that gives the type of information about the error
   * @private
   */
  rejectActionByID(theActionMgrID: string | number, allowDispatch = true, error?: ActionError) {
    const thePayload = this.actionMgrActions[theActionMgrID];
    let theReturn: string | number | null = theActionMgrID;

    if (thePayload) {
      // Call the promise reject function that was passed in as part of other payload.
      thePayload.reject(error);
      IsLoadingManagerInstance.disableLoadingIndicator(thePayload.actionPayload, allowDispatch);
    } else {
      theReturn = null;
    }

    return theReturn;
  }

  /**
   * This method is to add remote system id in request headers
   * @param remoteHeader - Remote system id to add in request headers.
   * @param actionConfig - Action config.
   * @function
   * @private
   */
  setRemoteCaseHeaderByContext(actionConfig: ActionObject, remoteHeader: string | undefined) {
    if (remoteHeader) {
      actionConfig.headers = actionConfig.headers || {};
      actionConfig.headers.RemoteSystemID = remoteHeader;
    }
  }

  /**
   * This method is to add necessary parameters for traditional remote case
   * @param actionConfig
   * @param className
   * @param remoteClassName
   * @returns string
   * @private
   */
  setTraditionalRemoteCaseParams(
    actionConfig: ExtendedActionObject,
    remoteCaseActionDetails: ExtendedActionObject
  ): string {
    actionConfig.className = remoteCaseActionDetails.className;
    actionConfig.isTraditionalRemoteCase = RemoteCase.isTraditionalRemoteCase(remoteCaseActionDetails.className);
    actionConfig.remoteClassName = remoteCaseActionDetails.remoteClassName;
    actionConfig.action = remoteCaseActionDetails.action;
    actionConfig.viewName = REMOTE_CASE_VIEW;
    actionConfig.gadgetId = remoteCaseActionDetails.gadgetId ?? CaseUtils.getUniqueGadgetId();
    actionConfig.name = remoteCaseActionDetails.name;
    actionConfig.skipSemanticUrl = true;
    return actionType.SHOW_PAGE;
  }
}

const ActionManagerInstance = new ActionManager();

// Expose ActionManagerInstance singleton so Nebula and other bridges can use those APIs
export { ActionManagerInstance };
